import api from './api';

const TOKEN_KEY = 'svi_auth_token';
const USER_KEY = 'svi_user';
const SSO_STATE_KEY = 'svi_sso_state';
const SSO_RETURN_KEY = 'svi_sso_return_to';
const SSO_LOGIN_KEY = 'svi_signed_in_via_sso';

// Only allow same-site relative paths, so a crafted link can't redirect elsewhere.
const isSafeReturnPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\');

class AuthService {
  async login(email, password) {
    const response = await api.post('/api/auth/login', { email, password });
    if (response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  async signup(userData) {
    const response = await api.post('/api/auth/signup', userData);
    if (response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SSO_LOGIN_KEY);
  }

  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }

  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  async getCurrentUser() {
    if (!this.isAuthenticated()) {
      return null;
    }
    try {
      const response = await api.get('/api/auth/me');
      this.setUser(response.data);
      return response.data;
    } catch (error) {
      // Token might be invalid
      this.logout();
      return null;
    }
  }

  // --- SSO (AWS Cognito) ---

  wasSignedInViaSSO() {
    return localStorage.getItem(SSO_LOGIN_KEY) === '1';
  }

  // URL that ends the Cognito session too; null if it can't be fetched (logout still proceeds).
  async getSSOLogoutUrl() {
    try {
      const response = await api.get('/api/auth/sso/logout-url');
      return response.data.logoutUrl || null;
    } catch (error) {
      return null;
    }
  }

  async getSSOStatus() {
    try {
      const response = await api.get('/api/auth/sso/status');
      return !!response.data.ssoEnabled;
    } catch (error) {
      return false;
    }
  }

  // Sends the browser to Cognito. A random nonce is sent as `state` and checked
  // again in handleSSOCallback, so a forged callback can't log someone in.
  async startSSO(returnTo) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    const state = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    sessionStorage.setItem(SSO_STATE_KEY, state);
    if (isSafeReturnPath(returnTo)) {
      sessionStorage.setItem(SSO_RETURN_KEY, returnTo);
    } else {
      sessionStorage.removeItem(SSO_RETURN_KEY);
    }
    const response = await api.get('/api/auth/sso/authorize', { params: { state } });
    window.location.assign(response.data.authorizationUrl);
  }

  // Reads `#token=...&state=...` from the callback URL. Returns the path to open next.
  handleSSOCallback(hash) {
    const params = new URLSearchParams((hash || '').replace(/^#/, ''));
    const token = params.get('token');
    const state = params.get('state');
    const expectedState = sessionStorage.getItem(SSO_STATE_KEY);
    const returnTo = sessionStorage.getItem(SSO_RETURN_KEY);
    sessionStorage.removeItem(SSO_STATE_KEY);
    sessionStorage.removeItem(SSO_RETURN_KEY);

    if (!token || !state || !expectedState || state !== expectedState) {
      throw new Error('SSO sign-in could not be verified. Please try again.');
    }
    this.setToken(token);
    localStorage.setItem(SSO_LOGIN_KEY, '1');
    return isSafeReturnPath(returnTo) ? returnTo : '/dashboard';
  }

  async requestPasswordReset(email) {
    const response = await api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async validateResetToken(token) {
    const response = await api.post('/api/auth/validate-reset-token', { token });
    return response.data;
  }

  async resetPassword(token, newPassword) {
    const response = await api.post('/api/auth/reset-password', { token, newPassword });
    return response.data;
  }
}

export default new AuthService();

